import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { feedbackApi, type FeedbackKind } from '@/lib/feedbackApi';
import { Button } from '@/components/ui/button';

/**
 * Per-item advisor feedback affordance for a registry row (a mode, panel,
 * biomarker, …). The MA can't edit the seed-authored value, but can flag it as
 * wrong or leave a note. Badge shows the open-flag count; the modal lists
 * existing feedback and lets the advisor add or resolve. Registry values are
 * never mutated here — this is the review/feedback channel.
 */
export function FeedbackControl({
  registry, itemId, itemLabel, openCount = 0, onChanged,
}: {
  registry: string; itemId: string; itemLabel: string; openCount?: number; onChanged?: () => void;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<FeedbackKind>('flag');
  const [message, setMessage] = useState('');

  const list = useQuery({
    queryKey: ['feedback', registry, itemId],
    queryFn: () => feedbackApi.list(registry, itemId),
    enabled: open,
  });

  const changed = () => {
    void qc.invalidateQueries({ queryKey: ['feedback', registry, itemId] });
    onChanged?.();
  };
  const add = useMutation({
    mutationFn: () => feedbackApi.create({ registry, item_id: itemId, item_label: itemLabel, kind, message: message.trim() }),
    onSuccess: () => { setMessage(''); changed(); },
  });
  const resolve = useMutation({
    mutationFn: (id: string) => feedbackApi.resolve(id),
    onSuccess: changed,
  });

  const stop = (e: React.MouseEvent) => { e.preventDefault(); e.stopPropagation(); };

  return (
    <>
      <button
        type="button"
        data-testid="feedback-trigger"
        onClick={(e) => { stop(e); setOpen(true); }}
        title="Advisor feedback"
        className={`inline-flex items-center gap-1 rounded-pill border px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap ${
          openCount > 0
            ? 'border-flagged bg-flagged-soft text-flagged'
            : 'border-border text-text-secondary hover:bg-surface-sunken'
        }`}
      >
        ⚑ {openCount > 0 ? openCount : 'Flag'}
      </button>

      {open ? (
        <div data-testid="feedback-modal" className="fixed inset-0 z-[1000] flex items-center justify-center bg-overlay" onClick={(e) => { stop(e); setOpen(false); }}>
          <div className="w-[560px] max-w-[92vw] rounded bg-surface p-21" onClick={stop}>
            <div className="mb-13 flex items-start justify-between gap-8">
              <div>
                <h3 className="text-h3 font-semibold">Advisor feedback</h3>
                <div className="text-body-sm text-text-secondary"><span className="font-mono">{itemId}</span> · {registry}</div>
              </div>
              <button type="button" onClick={(e) => { stop(e); setOpen(false); }} className="text-text-muted hover:text-text-primary">✕</button>
            </div>

            {/* existing feedback */}
            <div className="mb-13 max-h-[240px] overflow-y-auto">
              {list.isLoading ? <p className="py-2 text-body-sm text-text-muted">Loading…</p> : null}
              {list.data && list.data.length === 0 ? <p className="py-2 text-body-sm text-text-muted">No feedback yet.</p> : null}
              {list.data && list.data.length > 0 ? (
                <ul className="divide-y divide-border">
                  {list.data.map((f) => (
                    <li key={f.id} className={`py-2 ${f.status === 'resolved' ? 'opacity-55' : ''}`}>
                      <div className="flex items-center justify-between gap-8">
                        <span className={`rounded-pill px-2 py-0.5 text-[10px] font-semibold uppercase ${f.kind === 'flag' ? 'bg-flagged-soft text-flagged' : 'bg-surface-sunken text-text-secondary'}`}>
                          {f.kind}{f.status === 'resolved' ? ' · resolved' : ''}
                        </span>
                        {f.status === 'open' ? (
                          <Button variant="outline" size="sm" disabled={resolve.isPending} onClick={(e) => { stop(e); resolve.mutate(f.id); }}>Resolve</Button>
                        ) : null}
                      </div>
                      <p className="mt-1 text-body-sm">{f.message}</p>
                      <div className="text-[11px] text-text-muted">{f.reviewer} · {f.timestamp}</div>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>

            {/* add feedback */}
            <div className="rounded border border-border bg-bg p-13">
              <div className="mb-8 flex gap-8">
                <label className="flex items-center gap-1 text-body-sm">
                  <input type="radio" checked={kind === 'flag'} onChange={() => setKind('flag')} /> Flag an error
                </label>
                <label className="flex items-center gap-1 text-body-sm">
                  <input type="radio" checked={kind === 'note'} onChange={() => setKind('note')} /> Note
                </label>
              </div>
              <textarea
                data-testid="feedback-message"
                value={message} onChange={(e) => setMessage(e.target.value)}
                placeholder={kind === 'flag' ? 'What is wrong with this entry?' : 'Add a note for the record…'}
                className="min-h-[64px] w-full resize-y rounded border border-border-strong bg-surface px-13 py-8 text-body-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus"
              />
              <div className="mt-8 flex items-center justify-end gap-8">
                {add.isError ? <span className="mr-auto text-body-sm text-flagged">Could not save.</span> : null}
                <Button size="sm" disabled={add.isPending || !message.trim()} onClick={(e) => { stop(e); add.mutate(); }}>
                  {add.isPending ? 'Saving…' : kind === 'flag' ? 'Flag error' : 'Add note'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
